import React, {useEffect, useMemo, useRef, useState} from "react";
import {Helmet, HelmetProvider} from "react-helmet-async";
import AdminLayout from "../components/AdminLayout";
import {BASE_URLS} from "../../config/urls";
import {apiFetch} from "../../utils/api";
import "../styles/admin-supplies.css";

import {SupplyDTO} from "../../models/SupplyDTO";
import {ScheduledSupplyDTO} from "../../models/ScheduledSupplyDTO";
import {ScheduledSupplyTemplateDTO} from "../../models/ScheduledSupplyTemplateDTO";

import {DOW_OPTIONS} from "../components/supply/constants";
import {ModalMode, RelatedOrderDTO, SelectOption, WarehouseFormRow} from "../components/supply/types";
import {addDays, addWeeks, nextDateWithDow, startOfIsoWeek, toDatePickerDow} from "../components/supply/utils/date";
import {isOzonFlag} from "../components/supply/utils/format";

import {SuppliesHeader} from "../components/supply/sections/SuppliesHeader";
import {TypesSection} from "../components/supply/sections/TypesSection";
import {TemplatesSection} from "../components/supply/sections/TemplatesSection";
import {ScheduledSection} from "../components/supply/sections/ScheduledSection";
import {CurrentSection} from "../components/supply/sections/CurrentSection";

import {TypeModal} from "../components/supply/modals/TypeModal";
import {TemplateModal} from "../components/supply/modals/TemplateModal";
import {ScheduledModal} from "../components/supply/modals/ScheduledModal";
import {DeleteModal} from "../components/supply/modals/DeleteModal";

type SupplyTypesResponse = SupplyDTO[] | { items: SupplyDTO[] };

export default function AdminSuppliesPage() {
    // ====== TYPES (structure) ======
    const [types, setTypes] = useState<SupplyDTO[]>([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);

    const windowStart = useMemo(() => addWeeks(startOfIsoWeek(new Date()), 3), []);
    const windowEnd = useMemo(() => addDays(windowStart, 6), [windowStart]);

    // type modal
    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
    const [typeModalMode, setTypeModalMode] = useState<ModalMode>("edit");
    const [editingType, setEditingType] = useState<SupplyDTO | null>(null);
    const [typeTitle, setTypeTitle] = useState("");
    const [typeOzon, setTypeOzon] = useState(false);
    const [typeWarehouses, setTypeWarehouses] = useState<WarehouseFormRow[]>([]);
    const [isSavingType, setIsSavingType] = useState(false);

    // ====== TEMPLATES (schedule rules) ======
    const [templates, setTemplates] = useState<ScheduledSupplyTemplateDTO[]>([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [templateModalMode, setTemplateModalMode] = useState<ModalMode>("create");
    const [editingTemplate, setEditingTemplate] = useState<ScheduledSupplyTemplateDTO | null>(null);

    const [templateTypeId, setTemplateTypeId] = useState<number | null>(null);
    const [templateDepartureDow, setTemplateDepartureDow] = useState<SelectOption | null>(null);
    const [templateAcceptanceDow, setTemplateAcceptanceDow] = useState<SelectOption | null>(null);
    const [templateIntervalWeeks, setTemplateIntervalWeeks] = useState<1 | 2>(1);
    const [templatePhase, setTemplatePhase] = useState<0 | 1>(0);
    const [isSavingTemplate, setIsSavingTemplate] = useState(false);

    // ====== SCHEDULED (window items) ======
    const [scheduled, setScheduled] = useState<ScheduledSupplyDTO[]>([]);
    const [isLoadingScheduled, setIsLoadingScheduled] = useState(false);

    const [isScheduledModalOpen, setIsScheduledModalOpen] = useState(false);
    const [scheduledModalMode, setScheduledModalMode] = useState<ModalMode>("create");
    const [editingScheduled, setEditingScheduled] = useState<ScheduledSupplyDTO | null>(null);

    const [scheduledTemplateId, setScheduledTemplateId] = useState<number | null>(null);
    const [scheduledDepartureDate, setScheduledDepartureDate] = useState<Date | null>(null);
    const [scheduledAcceptanceDate, setScheduledAcceptanceDate] = useState<Date | null>(null);
    const [isSavingScheduled, setIsSavingScheduled] = useState(false);

    // ====== CURRENT supplies ======
    const [currentSupplies, setCurrentSupplies] = useState<SupplyDTO[]>([]);
    const [isLoadingCurrent, setIsLoadingCurrent] = useState(false);

    // delete current/scheduled modal
    const [deleteItem, setDeleteItem] = useState<{
        id: number;
        title: string;
        kind: "current" | "scheduled";
    } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [relatedOrders, setRelatedOrders] = useState<RelatedOrderDTO[]>([]);
    const [isLoadingRelatedOrders, setIsLoadingRelatedOrders] = useState(false);

    // filters
    const [typesOzonFilter, setTypesOzonFilter] = useState(false);
    const [templatesOzonFilter, setTemplatesOzonFilter] = useState(false);
    const [scheduledOzonFilter, setScheduledOzonFilter] = useState(false);
    const [currentOzonFilter, setCurrentOzonFilter] = useState(false);

    const typesRef = useRef<HTMLDivElement | null>(null);
    const templatesRef = useRef<HTMLDivElement | null>(null);
    const scheduledRef = useRef<HTMLDivElement | null>(null);
    const currentRef = useRef<HTMLDivElement | null>(null);

    // ====== LOADS ======
    const reloadTypes = async () => {
        setIsLoadingTypes(true);
        try {
            const res = await apiFetch(`${BASE_URLS.api}/supplies/types`);
            const data: SupplyTypesResponse = await res.json();
            const arr = Array.isArray(data) ? data : (data as any)?.items ?? [];
            setTypes(Array.isArray(arr) ? arr : []);
        } catch (e) {
            console.error(e);
            setTypes([]);
        } finally {
            setIsLoadingTypes(false);
        }
    };

    const reloadTemplates = async () => {
        setIsLoadingTemplates(true);
        try {
            const res = await apiFetch(`${BASE_URLS.api}/supplies/templates`);
            const data: ScheduledSupplyTemplateDTO[] = await res.json();
            setTemplates(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            setTemplates([]);
        } finally {
            setIsLoadingTemplates(false);
        }
    };

    const reloadScheduled = async () => {
        setIsLoadingScheduled(true);
        try {
            const res = await apiFetch(`${BASE_URLS.api}/supplies/scheduled`);
            const data: ScheduledSupplyDTO[] = await res.json();
            setScheduled(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            setScheduled([]);
        } finally {
            setIsLoadingScheduled(false);
        }
    };

    const reloadCurrent = async () => {
        setIsLoadingCurrent(true);
        try {
            const res = await apiFetch(`${BASE_URLS.api}/schedule`);
            const data: SupplyDTO[] = await res.json();
            setCurrentSupplies(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            setCurrentSupplies([]);
        } finally {
            setIsLoadingCurrent(false);
        }
    };

    useEffect(() => {
        reloadTypes();
        reloadTemplates();
        reloadScheduled();
        reloadCurrent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ====== derived options ======
    const typeOptions = useMemo<SelectOption[]>(
        () => (types as any[]).map((t) => ({value: String(t.id), label: t.title})),
        [types]
    );

    const typesLabelById = (id: number) => (types as any[]).find((x) => x.id === id)?.title || "—";

    // Текст в выпадающем списке "шаблонов" (чтобы пользователь сразу видел расписание)
    const templateOptions = useMemo<SelectOption[]>(() => {
        return templates.map((t) => {
            const depLabel = DOW_OPTIONS.find((d) => d.value === String(t.departureDow))?.label ?? "?";
            const intervalText = t.intervalWeeks === 2 ? `2н, фаза ${t.phase}` : "еженед";
            return {
                value: String(t.id),
                label: `${t.title} — ${depLabel} (${intervalText})`,
            };
        });
    }, [templates]);

    // filters
    const typesToShow = (types as any[]).filter((t) => isOzonFlag(t) === typesOzonFilter);
    const templatesToShow = (templates as any[]).filter((t) => isOzonFlag(t) === templatesOzonFilter);
    const scheduledToShow = (scheduled as any[]).filter((s) => isOzonFlag(s) === scheduledOzonFilter);
    const currentToShow = (currentSupplies as any[]).filter((s) => isOzonFlag(s) === currentOzonFilter);

    // ====== выбранный шаблон для модалки scheduled ======
    const selectedScheduledTemplate = useMemo(() => {
        if (!scheduledTemplateId) return null;
        return templates.find((t) => t.id === scheduledTemplateId) || null;
    }, [scheduledTemplateId, templates]);

    const closeTypeModal = () => {
        setIsTypeModalOpen(false);
        setIsSavingType(false);
    };

    const addTypeWarehouseRow = () => setTypeWarehouses((p) => [...p, {
        warehouseName: "",
        store: null,
        sendCity: "",
        price: ""
    }]);
    const updateTypeWarehouseRow = (idx: number, patch: Partial<WarehouseFormRow>) =>
        setTypeWarehouses((p) => p.map((r, i) => (i === idx ? {...r, ...patch} : r)));
    const removeTypeWarehouseRow = (idx: number) =>
        setTypeWarehouses((p) => (p.length <= 1 ? p : p.filter((_, i) => i !== idx)));

    const resetTypeForm = () => {
        setEditingType(null);
        setTypeTitle("");
        setTypeOzon(false);
        setTypeWarehouses([{warehouseName: "", store: null, sendCity: "", price: ""}]);
    };

    const openCreateType = () => {
        setTypeModalMode("create");
        resetTypeForm();
        setIsTypeModalOpen(true);
    };

    const saveType = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSavingType) return;

        const editingId = (editingType as any)?.id ?? null;

        if (typeModalMode === "edit" && !editingId) {
            alert("Тип не выбран для редактирования");
            return;
        }

        if (!typeTitle.trim()) return alert("Укажите название");
        if (typeWarehouses.length === 0) return alert("Добавьте хотя бы один склад");

        if (typeWarehouses.some((w) => !w.warehouseName.trim())) return alert("Укажите название склада для каждой строки");
        if (typeWarehouses.some((w) => !w.store?.value)) return alert("Выберите тип склада для каждой строки");
        if (typeWarehouses.some((w) => !w.sendCity.trim())) return alert("Укажите город отправки для каждого склада");
        if (typeWarehouses.some((w) => !w.price.trim()))
            return alert("Укажите цену склада для каждой строки");

        if (typeWarehouses.some((w) => Number.isNaN(Number(w.price)) || Number(w.price) <= 0))
            return alert("Цена склада должна быть числом больше 0");

        if (typeOzon) {
            const invalid = typeWarehouses.some((w) => w.store!.value !== "Ozon" && w.store!.value !== "Яндекс Маркет");
            if (invalid) return alert("Для Ozon/ЯМ склады должны быть Ozon или Яндекс Маркет");
        } else {
            const invalid = typeWarehouses.some((w) => w.store!.value !== "Wildberries");
            if (invalid) return alert("Для WB все склады должны быть Wildberries");
        }

        const payload: any = {
            title: typeTitle.trim(),
            ozon: typeOzon,
            warehouses: typeWarehouses.map((w) => ({
                id: w.id,
                warehouseName: w.warehouseName.trim(),
                store: w.store?.value || "",
                sendCity: w.sendCity.trim(),
                price: Number(w.price),
            })),
        };

        if (typeModalMode === "edit") payload.id = editingId;

        const url =
            typeModalMode === "create"
                ? `${BASE_URLS.api}/supplies/types`
                : `${BASE_URLS.api}/supplies/types/${editingId}`;

        const method = typeModalMode === "create" ? "POST" : "PUT";

        try {
            setIsSavingType(true);

            const res = await apiFetch(url, {
                method,
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const t = await res.text();
                throw new Error(t || String(res.status));
            }

            await reloadTypes();
            await reloadTemplates();
            await reloadScheduled();

            closeTypeModal();
        } catch (err: any) {
            console.error(err);
            alert(err.message || "Не удалось сохранить тип");
            setIsSavingType(false);
        }
    };

    // ====== TEMPLATE modal ======
    const resetTemplateForm = () => {
        setEditingTemplate(null);
        setTemplateTypeId(null);
        setTemplateDepartureDow(null);
        setTemplateAcceptanceDow(null);
        setTemplateIntervalWeeks(1);
        setTemplatePhase(0);
    };

    const openCreateTemplate = () => {
        setTemplateModalMode("create");
        resetTemplateForm();
        setIsTemplateModalOpen(true);
    };

    const openEditTemplate = (t: ScheduledSupplyTemplateDTO) => {
        setTemplateModalMode("edit");
        setEditingTemplate(t);
        setTemplateTypeId(t.typeId);
        setTemplateDepartureDow(DOW_OPTIONS.find((x) => x.value === String(t.departureDow)) || null);
        setTemplateAcceptanceDow(DOW_OPTIONS.find((x) => x.value === String(t.acceptanceDow)) || null);
        setTemplateIntervalWeeks(t.intervalWeeks);
        setTemplatePhase(t.phase);
        setIsTemplateModalOpen(true);
    };

    const closeTemplateModal = () => {
        setIsTemplateModalOpen(false);
        setIsSavingTemplate(false);
    };

    const saveTemplate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSavingTemplate) return;

        if (!templateTypeId) return alert("Выберите тип");
        if (!templateDepartureDow) return alert("Выберите день отправки");
        if (!templateAcceptanceDow) return alert("Выберите день приёмки");

        const payload: any = {
            typeId: templateTypeId,
            departureDow: Number(templateDepartureDow.value),
            acceptanceDow: Number(templateAcceptanceDow.value),
            intervalWeeks: templateIntervalWeeks,
            phase: templateIntervalWeeks === 2 ? templatePhase : 0,
        };

        try {
            setIsSavingTemplate(true);

            const url =
                templateModalMode === "create"
                    ? `${BASE_URLS.api}/supplies/templates`
                    : `${BASE_URLS.api}/supplies/templates/${editingTemplate!.id}`;

            const method = templateModalMode === "create" ? "POST" : "PUT";

            const res = await apiFetch(url, {
                method,
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(String(res.status));

            await reloadTemplates();
            closeTemplateModal();
        } catch (err) {
            console.error(err);
            alert("Не удалось сохранить шаблон");
            setIsSavingTemplate(false);
        }
    };

    const deleteTemplate = async (id: number) => {
        if (
            !window.confirm(
                "Удалить шаблон?\nЭто повлияет на будущие планируемые поставки, которые строятся по этому шаблону."
            )
        )
            return;

        try {
            const res = await apiFetch(`${BASE_URLS.api}/supplies/templates/${id}`, {method: "DELETE"});
            if (!res.ok) throw new Error(String(res.status));

            await reloadTemplates();
            await reloadScheduled();
        } catch (err) {
            console.error(err);
            alert("Не удалось удалить шаблон");
        }
    };

    const deleteType = async (t: any) => {
        if (!t?.id) return;

        const ok = window.confirm(
            `Удалить тип "${t.title ?? "—"}"?\n` +
            "Его нельзя удалять, пока есть текущие или планируемые поставки или шаблоны с этим типом. Все заказы останутся в лк пользователей и админке заказов"
        );
        if (!ok) return;

        try {
            const res = await apiFetch(`${BASE_URLS.api}/supplies/types/${t.id}`, {method: "DELETE"});
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || String(res.status));
            }

            await reloadTypes();
            await reloadTemplates();
            await reloadScheduled();
        } catch (e: any) {
            console.error(e);
            alert(e.message || "Не удалось удалить тип");
        }
    };

    // ====== SCHEDULED modal ======
    const resetScheduledForm = () => {
        setEditingScheduled(null);
        setScheduledTemplateId(null);
        setScheduledDepartureDate(null);
        setScheduledAcceptanceDate(null);
    };

    const openCreateScheduled = () => {
        setScheduledModalMode("create");
        resetScheduledForm();
        setIsScheduledModalOpen(true);
    };

    const closeScheduledModal = () => {
        setIsScheduledModalOpen(false);
        setIsSavingScheduled(false);
    };

    // UX: авто-подстановка ближайших дат после выбора шаблона (только create)
    useEffect(() => {
        if (!selectedScheduledTemplate) return;
        if (scheduledModalMode !== "create") return;
        if (scheduledDepartureDate || scheduledAcceptanceDate) return;

        const depAllowed = toDatePickerDow(selectedScheduledTemplate.departureDow);
        const accAllowed = toDatePickerDow(selectedScheduledTemplate.acceptanceDow);

        // ВАЖНО: стартуем от windowStart, а не от today
        const dep = nextDateWithDow(windowStart, depAllowed, 6);
        const acc = nextDateWithDow(windowStart, accAllowed, 6);

        setScheduledDepartureDate(dep ?? null);
        setScheduledAcceptanceDate(acc ?? null);
    }, [
        selectedScheduledTemplate,
        scheduledModalMode,
        scheduledDepartureDate,
        scheduledAcceptanceDate,
        windowStart, // добавили зависимость
    ]);
    const saveScheduled = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSavingScheduled) return;

        if (!scheduledTemplateId) return alert("Выберите шаблон");
        if (!scheduledDepartureDate) return alert("Выберите дату отправки");
        if (!scheduledAcceptanceDate) return alert("Выберите дату приёмки");

        if (scheduledAcceptanceDate.getTime() > scheduledDepartureDate.getTime()) {
            return alert("Дата приёмки должна быть раньше даты отправки (или в тот же день)");
        }

        const payload = {
            templateId: scheduledTemplateId,
            departureDate: scheduledDepartureDate,
            acceptanceDate: scheduledAcceptanceDate,
        };

        try {
            setIsSavingScheduled(true);

            const url =
                scheduledModalMode === "create"
                    ? `${BASE_URLS.api}/supplies/scheduled`
                    : `${BASE_URLS.api}/supplies/scheduled/${editingScheduled!.id}`;

            const method = scheduledModalMode === "create" ? "POST" : "PUT";

            const res = await apiFetch(url, {
                method,
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || "Ошибка сохранения");
            }

            await reloadScheduled();
            closeScheduledModal();
        } catch (err: any) {
            console.error(err);
            alert(err.message);
            setIsSavingScheduled(false);
        }
    };

    // ====== delete current/scheduled ======
    const requestDelete = async (item: any, kind: "current" | "scheduled") => {
        setDeleteItem({id: item.id, title: item.title ?? "—", kind});
        setRelatedOrders([]);
        setIsLoadingRelatedOrders(false);

        if (kind === "current") {
            try {
                setIsLoadingRelatedOrders(true);
                const res = await apiFetch(`${BASE_URLS.api}/schedule/${item.id}/orders`);
                if (!res.ok) throw new Error(String(res.status));
                const data: RelatedOrderDTO[] = await res.json();
                setRelatedOrders(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error(e);
                setRelatedOrders([]);
            } finally {
                setIsLoadingRelatedOrders(false);
            }
        }
    };

    const closeDeleteModal = () => {
        setDeleteItem(null);
        setIsDeleting(false);
        setRelatedOrders([]);
        setIsLoadingRelatedOrders(false);
    };

    const confirmDelete = async () => {
        if (!deleteItem) return;

        try {
            setIsDeleting(true);

            const url =
                deleteItem.kind === "current"
                    ? `${BASE_URLS.api}/schedule/${deleteItem.id}`
                    : `${BASE_URLS.api}/supplies/scheduled/${deleteItem.id}`;

            const res = await apiFetch(url, {method: "DELETE"});
            if (!res.ok) throw new Error(String(res.status));

            if (deleteItem.kind === "current") await reloadCurrent();
            else await reloadScheduled();

            closeDeleteModal();
        } catch (e) {
            console.error(e);
            alert("Не удалось удалить");
            setIsDeleting(false);
        }
    };

    // ====== scroll ======
    const scrollTo = (ref: any) => ref.current?.scrollIntoView({behavior: "smooth", block: "start"});

    // ====== select portal (react-select) ======
    const selectMenuPortal = useMemo(
        () => ({
            menuPortalTarget: document.body,
            menuPosition: "fixed" as const,
            styles: {
                menuPortal: (base: any) => ({...base, zIndex: 9999}),
            },
        }),
        []
    );

    // ====== UI ======
    return (
        <AdminLayout isPopupVisible={isTypeModalOpen || isTemplateModalOpen || isScheduledModalOpen || !!deleteItem}>
            <HelmetProvider>
                <Helmet title="Поставки"/>
            </HelmetProvider>

            <div className="page_content supplies-page">
                <section className="admin-card" style={{marginTop: 90}}>
                    <h1 className="admin-heading">Поставки</h1>
                    <p className="admin-subtitle">
                        Добавление, редактирование и удаление планируемых и текущих поставок.
                        Каждый понедельник в 00:00 планируемые поставки добавляются в расписание, и по шаблонам
                        формируются новые планируемые поставки.
                    </p>

                    <SuppliesHeader
                        onCreateType={openCreateType}
                        onCreateTemplate={openCreateTemplate}
                        onCreateScheduled={openCreateScheduled}
                        onScrollTypes={() => scrollTo(typesRef)}
                        onScrollTemplates={() => scrollTo(templatesRef)}
                        onScrollScheduled={() => scrollTo(scheduledRef)}
                        onScrollCurrent={() => scrollTo(currentRef)}
                    />

                    <TypesSection
                        innerRef={typesRef}
                        typesToShow={typesToShow}
                        isLoading={isLoadingTypes}
                        ozonFilter={typesOzonFilter}
                        setOzonFilter={setTypesOzonFilter}
                        onDeleteType={deleteType}
                    />

                    <TemplatesSection
                        innerRef={templatesRef}
                        templatesToShow={templatesToShow}
                        isLoading={isLoadingTemplates}
                        ozonFilter={templatesOzonFilter}
                        setOzonFilter={setTemplatesOzonFilter}
                        onEdit={openEditTemplate}
                        onDelete={deleteTemplate}
                    />

                    <ScheduledSection
                        innerRef={scheduledRef}
                        scheduledToShow={scheduledToShow}
                        isLoading={isLoadingScheduled}
                        ozonFilter={scheduledOzonFilter}
                        setOzonFilter={setScheduledOzonFilter}
                        onDeleteRequest={(s) => requestDelete(s, "scheduled")}
                    />

                    <CurrentSection
                        innerRef={currentRef}
                        currentToShow={currentToShow}
                        isLoading={isLoadingCurrent}
                        ozonFilter={currentOzonFilter}
                        setOzonFilter={setCurrentOzonFilter}
                        onDeleteRequest={(s) => requestDelete(s, "current")}
                    />
                </section>

                {/* ================= TYPE MODAL ================= */}
                {isTypeModalOpen && (
                    <TypeModal
                        mode={typeModalMode}
                        typeTitle={typeTitle}
                        setTypeTitle={setTypeTitle}
                        typeOzon={typeOzon}
                        setTypeOzon={setTypeOzon}
                        typeWarehouses={typeWarehouses}
                        onAddWarehouseRow={addTypeWarehouseRow}
                        onUpdateWarehouseRow={updateTypeWarehouseRow}
                        onRemoveWarehouseRow={removeTypeWarehouseRow}
                        onClose={closeTypeModal}
                        onSubmit={saveType}
                        isSaving={isSavingType}
                        selectMenuPortal={selectMenuPortal}
                    />
                )}

                {/* ================= TEMPLATE MODAL ================= */}
                {isTemplateModalOpen && (
                    <TemplateModal
                        mode={templateModalMode}
                        typeOptions={typeOptions}
                        typesLabelById={typesLabelById}
                        templateTypeId={templateTypeId}
                        setTemplateTypeId={setTemplateTypeId}
                        templateDepartureDow={templateDepartureDow}
                        setTemplateDepartureDow={setTemplateDepartureDow}
                        templateAcceptanceDow={templateAcceptanceDow}
                        setTemplateAcceptanceDow={setTemplateAcceptanceDow}
                        templateIntervalWeeks={templateIntervalWeeks}
                        setTemplateIntervalWeeks={setTemplateIntervalWeeks}
                        templatePhase={templatePhase}
                        setTemplatePhase={setTemplatePhase}
                        onClose={closeTemplateModal}
                        onSubmit={saveTemplate}
                        isSaving={isSavingTemplate}
                        selectMenuPortal={selectMenuPortal}
                    />
                )}

                {/* ================= SCHEDULED MODAL ================= */}
                {isScheduledModalOpen && (
                    <ScheduledModal
                        mode={scheduledModalMode}
                        templateOptions={templateOptions}
                        scheduledTemplateId={scheduledTemplateId}
                        setScheduledTemplateId={setScheduledTemplateId}
                        selectedTemplate={selectedScheduledTemplate}
                        scheduledDepartureDate={scheduledDepartureDate}
                        setScheduledDepartureDate={setScheduledDepartureDate}
                        scheduledAcceptanceDate={scheduledAcceptanceDate}
                        setScheduledAcceptanceDate={setScheduledAcceptanceDate}
                        windowStart={windowStart}
                        windowEnd={windowEnd}
                        onClose={closeScheduledModal}
                        onSubmit={saveScheduled}
                        isSaving={isSavingScheduled}
                        selectMenuPortal={selectMenuPortal}
                    />
                )}

                {/* ================= DELETE MODAL ================= */}
                {deleteItem && (
                    <DeleteModal
                        item={deleteItem}
                        isDeleting={isDeleting}
                        relatedOrders={relatedOrders}
                        isLoadingRelatedOrders={isLoadingRelatedOrders}
                        onClose={closeDeleteModal}
                        onConfirm={confirmDelete}
                    />
                )}
            </div>
        </AdminLayout>
    );
}
